import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Check } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { registerInterest } from "../api";

const PROVINCES = [
  "Alberta", "British Columbia", "Manitoba", "New Brunswick",
  "Newfoundland and Labrador", "Northwest Territories", "Nova Scotia",
  "Nunavut", "Ontario", "Prince Edward Island", "Quebec", "Saskatchewan", "Yukon",
];

const EMPTY = { name: "", email: "", phone: "", city: "", province: "" };

export default function RegisterModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Lock background scroll while the modal is open, compensating for the
  // scrollbar width so the page behind doesn't shift. This also means that
  // when the Province <Select> opens, there is no scrollbar left for Radix's
  // scroll-lock to remove — preventing the hero image from resizing/shifting.
  useEffect(() => {
    if (!open) return undefined;
    const sw = window.innerWidth - document.documentElement.clientWidth;
    const prevOverflow = document.body.style.overflow;
    const prevPadding = document.body.style.paddingRight;
    document.body.style.overflow = "hidden";
    // Always set paddingRight to lock it in place (14px base + scrollbar width)
    document.body.style.paddingRight = `${14 + sw}px`;
    return () => {
      document.body.style.overflow = prevOverflow;
      document.body.style.paddingRight = prevPadding;
    };
  }, [open]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const close = () => {
    onClose();
    setTimeout(() => { setDone(false); setForm(EMPTY); setError(""); }, 300);
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.phone || !form.city || !form.province) {
      setError("Please complete all fields.");
      return;
    }
    setLoading(true);
    try {
      await registerInterest(form);
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={close}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <button className="modal-close" aria-label="Close" onClick={close}><X size={20} /></button>
        {!done ? (
          <>
            <span className="eyebrow">Register Interest</span>
            <h3 className="modal-title display">Be among the <em>first</em>.</h3>
            <p className="modal-sub">Share a few details and we'll keep you posted on openings, tours, and first access to Wynridge.</p>
            <form className="rf" onSubmit={submit}>
              <div className="rf-field">
                <label>Full name</label>
                <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Jane Doe" />
              </div>
              <div className="rf-row">
                <div className="rf-field">
                  <label>Email</label>
                  <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="jane@email.com" />
                </div>
                <div className="rf-field">
                  <label>Phone</label>
                  <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="(555) 555-5555" />
                </div>
              </div>
              <div className="rf-row">
                <div className="rf-field">
                  <label>City</label>
                  <input value={form.city} onChange={(e) => set("city", e.target.value)} placeholder="Toronto" />
                </div>
                <div className="rf-field">
                  <label>Province</label>
                  <Select value={form.province} onValueChange={(v) => set("province", v)} modal={false}>
                    <SelectTrigger className="rf-select"><SelectValue placeholder="Select province" /></SelectTrigger>
                    <SelectContent className="z-[1100]">
                      {PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {error && <p className="rf-error">{error}</p>}
              <button type="submit" className="pill pill-dark" disabled={loading} style={{ width: "100%", justifyContent: "center" }}>
                {loading ? "Submitting\u2026" : "Register Your Interest"}
              </button>
              <p className="rf-consent">By submitting, you consent to be contacted about Wynridge. Read our <Link to="/privacy" onClick={close}>Privacy Policy</Link>.</p>
            </form>
          </>
        ) : (
          <div className="modal-done">
            <div className="modal-check"><Check size={30} strokeWidth={2.4} /></div>
            <h3 className="modal-title display">Thank you.</h3>
            <p className="modal-sub">You're on the list — we'll be in touch soon with news and first access to Wynridge.</p>
            <button className="pill pill-dark" onClick={close}>Close</button>
          </div>
        )}
      </div>
    </div>
  );
}
